/**
 * SupportCenter - User Support Dashboard
 * =======================================
 * Premium UI design with two-column layout
 * Stats bar, request list, and detail panel
 * 
 * @version 3.0
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MessageCircle, Plus, Clock, CheckCircle2, 
  AlertCircle, Send, Paperclip, Loader2, X, RefreshCw,
  HelpCircle, Search, Camera, CreditCard, Package,
  Star, FileText, Code, Users, Lightbulb, Filter,
  ChevronDown, Image as ImageIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../App';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Context icons mapping
const CONTEXT_ICONS = {
  image_editor: Camera,
  visual_search: Search,
  materials: Package,
  billing: CreditCard,
  general: HelpCircle,
  sre: Code,
  account: Users,
  platform: Lightbulb,
  api: Code
};

const CONTEXT_LABELS = {
  image_editor: 'Editor Immagini',
  visual_search: 'Ricerca Visiva',
  materials: 'Materiali',
  billing: 'Fatturazione',
  general: 'Generale',
  sre: 'SRE',
  account: 'Account',
  platform: 'Piattaforma',
  api: 'API'
};

// Status configuration with Italian labels - Dark palette only
const STATUS_CONFIG = {
  new: { 
    label: 'Nuovo', 
    bgColor: 'bg-[#1a1a1a]', 
    textColor: 'text-white/70',
    borderColor: 'border-[#2a2a2a]'
  },
  in_progress: { 
    label: 'In lavorazione', 
    bgColor: 'bg-[#B08B5C]/15', 
    textColor: 'text-[#B08B5C]',
    borderColor: 'border-[#B08B5C]/30'
  },
  awaiting_user: { 
    label: 'In attesa di risposta', 
    bgColor: 'bg-[#B08B5C]/15', 
    textColor: 'text-[#B08B5C]',
    borderColor: 'border-[#B08B5C]/30'
  },
  awaiting_response: { 
    label: 'In attesa di risposta', 
    bgColor: 'bg-[#B08B5C]/15', 
    textColor: 'text-[#B08B5C]',
    borderColor: 'border-[#B08B5C]/30'
  },
  response_received: { 
    label: 'Risposta ricevuta', 
    bgColor: 'bg-[#2a2a2a]', 
    textColor: 'text-[#7a7a7a]',
    borderColor: 'border-[#3a3a3a]'
  },
  resolved: { 
    label: 'Risolto', 
    bgColor: 'bg-[#1a1a1a]', 
    textColor: 'text-white/50',
    borderColor: 'border-[#2a2a2a]'
  },
  closed: { 
    label: 'Chiusa', 
    bgColor: 'bg-[#1a1a1a]', 
    textColor: 'text-white/40',
    borderColor: 'border-[#2a2a2a]'
  }
};

// Generate ticket ID from request id
const generateTicketId = (id) => {
  if (!id) return '#SR-0000';
  const numPart = id.slice(-4).toUpperCase();
  return `#SR-${numPart}`;
};

// Format relative time
const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const diffWeeks = Math.floor(diffDays / 7);
  
  if (diffMins < 1) return 'Adesso';
  if (diffMins < 60) return `${diffMins} min fa`;
  if (diffHours < 24) return `${diffHours} ore fa`;
  if (diffDays === 1) return '1 giorno fa';
  if (diffDays < 7) return `${diffDays} giorni fa`;
  if (diffWeeks === 1) return '1 settimana fa';
  return `${diffWeeks} settimane fa`;
};

/**
 * Stats Card Component - Dark theme
 */
const StatsCard = ({ icon: Icon, value, label, sublabel }) => (
  <div className="bg-[#121212] border border-[#1a1a1a] rounded-xl p-5 flex items-center gap-4">
    <div className="w-12 h-12 rounded-full bg-[#0a0a0a] border border-[#1a1a1a] flex items-center justify-center flex-shrink-0">
      <Icon className="w-5 h-5 text-[#B08B5C]" />
    </div>
    <div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-sm text-white/60">{label}</p>
      {sublabel && <p className="text-xs text-white/30">{sublabel}</p>}
    </div>
  </div>
);

/**
 * Request List Item Component - Dark theme
 */
const RequestListItem = ({ request, isSelected, onClick }) => {
  const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.new;
  const ContextIcon = CONTEXT_ICONS[request.context] || MessageCircle;
  const ticketId = generateTicketId(request.id);
  
  return (
    <div
      onClick={onClick}
      className={`p-4 border-b border-[#1a1a1a] cursor-pointer transition-all hover:bg-[#0a0a0a] ${
        isSelected ? 'bg-[#0a0a0a] border-l-2 border-l-[#B08B5C]' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#0a0a0a] border border-[#1a1a1a] flex items-center justify-center flex-shrink-0 mt-0.5">
          <ContextIcon className="w-5 h-5 text-white/40" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h4 className="text-white font-medium truncate text-sm">
              {request.subject}
            </h4>
            <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${statusConfig.bgColor} ${statusConfig.textColor}`}>
              {statusConfig.label}
            </span>
          </div>
          <div className="flex items-center gap-2 text-xs text-white/40">
            <span className="text-white/50">{CONTEXT_LABELS[request.context] || 'Generale'}</span>
            <span>•</span>
            <span>{ticketId}</span>
          </div>
          <p className="text-xs text-white/30 mt-1">
            {formatRelativeTime(request.created_at)}
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Request Detail Panel Component - Dark theme
 */
const RequestDetailPanel = ({ request, responses, onReply, user }) => {
  const [replyMessage, setReplyMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  
  if (!request) {
    return (
      <div className="flex-1 flex items-center justify-center text-white/30 bg-[#121212] rounded-xl border border-[#1a1a1a]">
        <div className="text-center">
          <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-20" />
          <p>Seleziona una richiesta per vedere i dettagli</p>
        </div>
      </div>
    );
  }
  
  const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.new;
  const ContextIcon = CONTEXT_ICONS[request.context] || HelpCircle;
  const ticketId = generateTicketId(request.id);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim()) return;
    
    setIsSending(true);
    try {
      await onReply(request.id, replyMessage);
      setReplyMessage('');
    } finally {
      setIsSending(false);
    }
  };
  
  return (
    <div className="flex-1 flex flex-col bg-[#121212] rounded-xl border border-[#1a1a1a] overflow-hidden">
      {/* Header */}
      <div className="p-5 border-b border-[#1a1a1a]">
        <div className="flex items-center justify-between mb-3">
          <span className={`px-3 py-1 rounded text-xs font-medium ${statusConfig.bgColor} ${statusConfig.textColor}`}>
            {statusConfig.label}
          </span>
          <div className="text-right">
            <p className="text-sm text-white/50">{ticketId}</p>
            <p className="text-xs text-white/30">Creato {formatRelativeTime(request.created_at)}</p>
          </div>
        </div>
        <h2 className="text-xl font-semibold text-white mb-2">{request.subject}</h2>
        <div className="flex items-center gap-2">
          <ContextIcon className="w-4 h-4 text-white/40" />
          <span className="text-sm text-white/50">{CONTEXT_LABELS[request.context] || 'Generale'}</span>
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {/* Original message */}
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-[#B08B5C]/20 flex items-center justify-center flex-shrink-0 text-sm font-medium text-[#B08B5C]">
            {user?.name?.[0]?.toUpperCase() || 'U'}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-white font-medium text-sm">Tu</span>
              <span className="text-xs text-white/30">{formatRelativeTime(request.created_at)}</span>
            </div>
            <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
              <p className="text-white/70 text-sm whitespace-pre-wrap">{request.message}</p>
            </div>
            
            {/* Additional details (if any) */}
            {request.details && (
              <div className="mt-3">
                <p className="text-xs text-white/40 mb-2">Dettagli aggiuntivi</p>
                <ul className="text-sm text-white/50 space-y-1">
                  {Object.entries(request.details).map(([key, value]) => (
                    <li key={key} className="flex items-start gap-2">
                      <span className="text-white/30">•</span>
                      <span>{key}: {value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Attachments (if any) */}
            {request.attachments && request.attachments.length > 0 && (
              <div className="mt-4">
                <p className="text-xs text-white/40 mb-2">Allegati</p>
                <div className="flex flex-wrap gap-2">
                  {request.attachments.map((attachment, index) => (
                    <div key={index} className="relative group">
                      {attachment.type?.startsWith('image/') ? (
                        <img 
                          src={attachment.url} 
                          alt={attachment.name}
                          className="w-20 h-20 object-cover rounded-lg border border-[#1a1a1a]"
                        />
                      ) : (
                        <div className="w-20 h-20 bg-[#0a0a0a] rounded-lg border border-[#1a1a1a] flex items-center justify-center">
                          <FileText className="w-6 h-6 text-white/30" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-white/30 mt-1">
                  {request.attachments.length} file allegat{request.attachments.length === 1 ? 'o' : 'i'}
                </p>
              </div>
            )}
          </div>
        </div>
        
        {/* Responses */}
        {responses.map((response) => (
          <div key={response.id} className="flex items-start gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-medium ${
              response.responder_type === 'admin' 
                ? 'bg-[#B08B5C] text-black' 
                : 'bg-[#B08B5C]/20 text-[#B08B5C]'
            }`}>
              {response.responder_type === 'admin' ? 'SR' : user?.name?.[0]?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className={`font-medium text-sm ${
                  response.responder_type === 'admin' ? 'text-[#B08B5C]' : 'text-white'
                }`}>
                  {response.responder_type === 'admin' ? 'Assistenza SlabReality' : 'Tu'}
                </span>
                <span className="text-xs text-white/30">{formatRelativeTime(response.created_at)}</span>
              </div>
              <div className={`rounded-xl p-4 border ${
                response.responder_type === 'admin' 
                  ? 'bg-[#B08B5C]/10 border-[#B08B5C]/20' 
                  : 'bg-[#0a0a0a] border-[#1a1a1a]'
              }`}>
                <p className="text-white/70 text-sm whitespace-pre-wrap">{response.message}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Reply input */}
      {request.status !== 'closed' && request.status !== 'resolved' && (
        <div className="p-4 border-t border-[#1a1a1a] bg-[#0a0a0a]">
          <p className="text-xs text-white/40 mb-2">Scrivi un messaggio</p>
          <form onSubmit={handleSubmit}>
            <textarea
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              rows={3}
              placeholder="Descrivi qui il tuo messaggio..."
              className="w-full px-4 py-3 rounded-lg bg-[#121212] border border-[#1a1a1a] text-white placeholder-white/30 focus:border-[#B08B5C] focus:outline-none resize-none text-sm mb-3"
            />
            <div className="flex items-center justify-between">
              <Button
                type="button"
                variant="outline"
                className="border-[#1a1a1a] text-white/50 hover:bg-[#121212] hover:text-white/70"
              >
                <Paperclip className="w-4 h-4 mr-2" />
                Allega file
              </Button>
              <Button
                type="submit"
                disabled={!replyMessage.trim() || isSending}
                className="bg-[#B08B5C] text-black hover:bg-[#C9A46E]"
              >
                {isSending ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Invia messaggio
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

/**
 * New Request Modal - Dark theme
 */
const NewRequestModal = ({ isOpen, onClose, onSubmit, user }) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [context, setContext] = useState('general');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) {
      toast.error('Compila tutti i campi');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await onSubmit({ subject, message, context });
      setSubject('');
      setMessage('');
      setContext('general');
      onClose();
    } catch (error) {
      console.error('Submit error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0a0a0a] rounded-2xl border border-[#1a1a1a] w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b border-[#1a1a1a]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#B08B5C]/20 flex items-center justify-center">
              <Plus className="w-5 h-5 text-[#B08B5C]" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Nuova richiesta</h3>
              <p className="text-white/40 text-sm">Descrivici come possiamo aiutarti</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Context selector */}
          <div>
            <label className="text-sm text-white/50 block mb-2">Area</label>
            <select
              value={context}
              onChange={(e) => setContext(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-[#121212] border border-[#1a1a1a] text-white focus:border-[#B08B5C] focus:outline-none"
            >
              {Object.entries(CONTEXT_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          
          {/* Subject */}
          <div>
            <label className="text-sm text-white/50 block mb-2">Oggetto</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Es. Problema con caricamento texture..."
              className="w-full px-4 py-3 rounded-lg bg-[#121212] border border-[#1a1a1a] text-white placeholder-white/30 focus:border-[#B08B5C] focus:outline-none"
            />
          </div>
          
          {/* Message */}
          <div>
            <label className="text-sm text-white/50 block mb-2">Descrizione</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="Descrivi il problema o la richiesta in dettaglio..."
              className="w-full px-4 py-3 rounded-lg bg-[#121212] border border-[#1a1a1a] text-white placeholder-white/30 focus:border-[#B08B5C] focus:outline-none resize-none"
            />
          </div>
          
          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 border-[#1a1a1a] text-white/50 hover:bg-[#121212]"
            >
              Annulla
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-[#B08B5C] text-black hover:bg-[#C9A46E]"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Invia richiesta
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

/**
 * Main SupportCenter Component
 */
const SupportCenter = () => {
  const navigate = useNavigate();
  const { user: authUser, profile, loading: authLoading } = useAuth();
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [responses, setResponses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Build user object from auth context
  const user = authUser ? {
    id: authUser.id,
    email: authUser.email,
    name: profile?.full_name || profile?.first_name || authUser.email?.split('@')[0]
  } : null;
  
  // Redirect if not authenticated (after auth loading completes)
  useEffect(() => {
    if (!authLoading && !authUser) {
      navigate('/login');
    }
  }, [authLoading, authUser, navigate]);
  
  // Fetch requests
  const fetchRequests = useCallback(async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${API}/api/support/requests?user_id=${user.id}`);
      const data = await response.json();
      
      if (data.success) {
        setRequests(data.requests || []);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Errore nel caricamento delle richieste');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);
  
  useEffect(() => {
    if (user?.id) {
      fetchRequests();
    }
  }, [user?.id, fetchRequests]);
  
  // Fetch request detail
  const fetchRequestDetail = async (requestId) => {
    try {
      const response = await fetch(
        `${API}/api/support/requests/${requestId}?user_id=${user.id}`
      );
      const data = await response.json();
      
      if (data.success) {
        setSelectedRequest(data.request);
        setResponses(data.responses || []);
      }
    } catch (error) {
      console.error('Error fetching detail:', error);
      toast.error('Errore nel caricamento della conversazione');
    }
  };
  
  // Create new request
  const handleCreateRequest = async ({ subject, message, context }) => {
    try {
      const response = await fetch(
        `${API}/api/support/requests?user_id=${user.id}&user_email=${user.email}&user_name=${user.name || ''}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subject, message, context })
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Richiesta inviata!');
        fetchRequests();
      } else {
        throw new Error(data.detail || 'Errore');
      }
    } catch (error) {
      toast.error("Errore nell'invio della richiesta");
      throw error;
    }
  };
  
  // Reply to request
  const handleReply = async (requestId, message) => {
    try {
      const response = await fetch(
        `${API}/api/support/requests/${requestId}/reply?user_id=${user.id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ request_id: requestId, message })
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Risposta inviata');
        fetchRequestDetail(requestId);
      } else {
        throw new Error(data.detail || 'Errore');
      }
    } catch (error) {
      toast.error("Errore nell'invio della risposta");
    }
  };
  
  // Calculate stats
  const stats = {
    total: requests.length,
    awaiting: requests.filter(r => r.status === 'awaiting_user' || r.status === 'awaiting_response' || r.status === 'in_progress').length,
    closed: requests.filter(r => r.status === 'closed' || r.status === 'resolved').length,
    reviews: requests.filter(r => r.rating).length
  };
  
  // Filter requests
  const filteredRequests = requests.filter(r => {
    const matchesSearch = searchTerm === '' || 
      r.subject.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  
  // Show loading while auth is checking
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#B08B5C]" />
      </div>
    );
  }
  
  if (!user) {
    return null;
  }
  
  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col">
      <Header />
      
      <main className="flex-1 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Centro Assistenza</h1>
              <p className="text-white/50">
                Le tue richieste di supporto e le risposte del team SlabReality
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={fetchRequests}
                className="border-[#1a1a1a] text-white/50 hover:bg-[#121212] hover:text-white/70"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => setShowNewModal(true)}
                className="bg-[#B08B5C] text-black hover:bg-[#C9A46E]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuova richiesta
              </Button>
            </div>
          </div>
          
          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatsCard 
              icon={MessageCircle} 
              value={stats.total} 
              label="Totali richieste"
              sublabel="Tutte le richieste inviate"
            />
            <StatsCard 
              icon={AlertCircle} 
              value={stats.awaiting} 
              label="In attesa di risposta"
              sublabel="Il team ti risponderà presto"
            />
            <StatsCard 
              icon={CheckCircle2} 
              value={stats.closed} 
              label="Chiuse"
              sublabel="Richieste completate"
            />
            <StatsCard 
              icon={Star} 
              value={stats.reviews} 
              label="Valutazioni"
              sublabel="Media feedback ricevuti"
            />
          </div>
          
          {/* Main Content - Two Column Layout */}
          <div className="flex gap-6 min-h-[600px]">
            
            {/* Left Panel - Request List */}
            <div className="w-full lg:w-[420px] flex-shrink-0">
              <div className="bg-[#121212] rounded-xl border border-[#1a1a1a] overflow-hidden h-full flex flex-col">
                
                {/* Filters */}
                <div className="p-4 border-b border-[#1a1a1a] space-y-3">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="w-full px-4 py-2.5 rounded-lg bg-[#0a0a0a] border border-[#1a1a1a] text-white/60 text-sm focus:border-[#B08B5C] focus:outline-none appearance-none pr-10"
                      >
                        <option value="all">Tutte le richieste</option>
                        <option value="new">Nuove</option>
                        <option value="in_progress">In lavorazione</option>
                        <option value="awaiting_user">In attesa risposta</option>
                        <option value="resolved">Risolte</option>
                        <option value="closed">Chiuse</option>
                      </select>
                      <ChevronDown className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                  </div>
                  <div className="relative">
                    <Search className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Cerca nelle richieste..."
                      className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-[#0a0a0a] border border-[#1a1a1a] text-white placeholder-white/30 text-sm focus:border-[#B08B5C] focus:outline-none"
                    />
                  </div>
                </div>
                
                {/* Request List */}
                <div className="flex-1 overflow-y-auto">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-20">
                      <Loader2 className="w-6 h-6 animate-spin text-[#B08B5C]" />
                    </div>
                  ) : filteredRequests.length === 0 ? (
                    <div className="text-center py-20 px-4">
                      <MessageCircle className="w-12 h-12 mx-auto text-white/20 mb-3" />
                      <p className="text-white/40 text-sm">Nessuna richiesta trovata</p>
                    </div>
                  ) : (
                    filteredRequests.map(request => (
                      <RequestListItem
                        key={request.id}
                        request={request}
                        isSelected={selectedRequest?.id === request.id}
                        onClick={() => fetchRequestDetail(request.id)}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>
            
            {/* Right Panel - Request Detail */}
            <RequestDetailPanel
              request={selectedRequest}
              responses={responses}
              onReply={handleReply}
              user={user}
            />
          </div>
        </div>
      </main>
      
      <Footer />
      
      {/* New Request Modal */}
      <NewRequestModal
        isOpen={showNewModal}
        onClose={() => setShowNewModal(false)}
        onSubmit={handleCreateRequest}
        user={user}
      />
    </div>
  );
};

export default SupportCenter;
